import { useCallback, useEffect, type ComponentType, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { expValEquals } from '@confluence/feature-experiments';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';

// oxlint-disable-next-line import/no-cycle
import { useApprovals } from './ApprovalsContext.tsx';
import { openApprovalsPaneForContext } from './approvalsPaneSidebar.ts';

const SOURCE_CONFLUENCE_APPROVALS_PARAM = 'source_confluence_approvals';
const useSourceConfluenceApprovalsParam = createSingleQueryParamHook(
	SOURCE_CONFLUENCE_APPROVALS_PARAM,
);

const i18n = defineMessages({
	alreadyApprovedTitle: {
		id: 'approvals.flag.already-approved.title',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "You've already approved this page",
		description:
			'Title for the info flag shown when a reviewer clicks an approval notification but has already approved the page',
	},
	alreadyRequestedChangesTitle: {
		id: 'approvals.flag.already-requested-changes.title',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "You've already requested changes on this page",
		description:
			'Title for the info flag shown when a reviewer clicks an approval notification but has already requested changes on the page',
	},
	changeDecisionAction: {
		id: 'approvals.flag.already-responded.change-decision',
		defaultMessage: 'Change your decision',
		description:
			'Action button label on the already-responded info flag that opens the approvals pane so the reviewer can change their decision',
	},
	approvalUnavailableTitle: {
		id: 'approvals.flag.approval-unavailable.title',
		defaultMessage: 'This approval request is no longer available',
		description:
			'Title for the warning flag shown when an approval request is no longer available (e.g. it was deleted or the user was removed as a reviewer)',
	},
	approvalUnavailableDescription: {
		id: 'approvals.flag.approval-unavailable.description',
		defaultMessage: 'It may have been deleted.',
		description:
			'Description for the warning flag shown when an approval request is no longer available',
	},
});

type ApprovalCtaState = 'none' | 'unavailable' | 'pending-review' | 'already-responded';

const ApprovalCtaFlagComponent: FC<WithFlagsProps> = ({ flags }) => {
	const { formatMessage } = useIntl();
	const sourceConfluenceApprovalsParam = useSourceConfluenceApprovalsParam();
	const { setQueryParams } = useRouteActions();
	const {
		approvalRequestLoading,
		isCurrentUserApprover,
		currentUserApprover,
		approvalRequest,
		contentId,
		contentStatus,
	} = useApprovals();
	const rightSidebarContext = useRightSidebarContext();

	const openApprovalsPane = useCallback(() => {
		openApprovalsPaneForContext({ contentId, contentStatus, rightSidebarContext });
	}, [contentId, contentStatus, rightSidebarContext]);

	useEffect(() => {
		if (approvalRequestLoading) {
			return;
		}

		const isFromApprovalsCta = sourceConfluenceApprovalsParam !== undefined;

		if (!isFromApprovalsCta) {
			return;
		}

		setQueryParams({ [SOURCE_CONFLUENCE_APPROVALS_PARAM]: undefined }, true);

		let state: ApprovalCtaState = 'none';
		if (!isCurrentUserApprover) {
			state = 'unavailable';
		} else if (currentUserApprover?.status === 'PENDING_REVIEW') {
			state = 'pending-review';
		} else if (currentUserApprover) {
			state = 'already-responded';
		}

		if (state === 'unavailable') {
			void flags.showWarningFlag({
				title: formatMessage(i18n.approvalUnavailableTitle),
				description: formatMessage(i18n.approvalUnavailableDescription),
				isAutoDismiss: true,
			});
			return;
		}

		if (
			state === 'pending-review' &&
			expValEquals('confluence_approvals_autoexpand_sidepanel', 'isEnabled', true)
		) {
			openApprovalsPane();
			return;
		}

		if (state === 'already-responded' && currentUserApprover) {
			const isApprovalInProgress = approvalRequest?.review?.reviewStatus === 'PENDING';
			const title =
				currentUserApprover.status === 'REVIEWER_APPROVED'
					? formatMessage(i18n.alreadyApprovedTitle)
					: formatMessage(i18n.alreadyRequestedChangesTitle);

			void flags.showInfoFlag({
				title,
				isAutoDismiss: true,
				...(isApprovalInProgress && {
					actions: [
						{
							content: formatMessage(i18n.changeDecisionAction),
							onClick: openApprovalsPane,
						},
					],
				}),
			});
		}
	}, [
		approvalRequestLoading,
		sourceConfluenceApprovalsParam,
		isCurrentUserApprover,
		currentUserApprover,
		approvalRequest,
		flags,
		formatMessage,
		openApprovalsPane,
		setQueryParams,
	]);

	return null;
};

/**
 * ApprovalCtaFlag
 *
 * Handles all flag logic for users arriving via an approvals notification CTA
 * (identified by the `source_confluence_approvals` query parameter).
 *
 * Three states are detected (in priority order):
 *
 * 1. **Unavailable** — the user is no longer an active reviewer (request cancelled or user
 *    removed). Shows a warning flag: "This approval request is no longer available."
 *
 * 2. **Pending review** — the user is still a pending reviewer. When the auto-expand experiment
 *    is enabled, opens the approvals pane so they can review the page.
 *
 * 3. **Already responded** — the user is still a reviewer but has already approved or
 *    requested changes. Shows an info flag with a status-specific title:
 *    - "You've already approved this page"
 *    - "You've already requested changes on this page"
 *    If the approval is still in progress, a "Change your decision" action button is added.
 *
 * 4. **None** — the approval context is inconsistent; no action is taken.
 *
 * The query param is always removed after being read to prevent duplicate flags.
 *
 * This component renders nothing to the DOM (`null`) and is SSR-safe: the flag logic only
 * runs inside a `useEffect` (client-side only), so it will not cause hydration mismatches.
 */
export const ApprovalCtaFlag: ComponentType<Omit<unknown, 'flags'>> =
	withFlags(ApprovalCtaFlagComponent);
